<template>
  <div class="password-login-form">
    <CAlert color="danger" v-if="error" class="mb-3">{{ error }}</CAlert>

    <CInput
      ref="email"
      :placeholder="$pgettext('auth.placeholder','E-mail')"
      v-model="v$.email.$model"
      :label="$gettext('E-mail')"
      :is-valid="!v$.email.$error"
      autocomplete="username email"
      @keyup.enter="login">
      <template v-slot:invalid-feedback>
        <div class="invalid-feedback d-block" v-for="error of v$.email.$errors" :key="error.$uid">
          <div class="error-msg">{{ error.$message }}</div>
        </div>
      </template>
    </CInput>

    <InputPassword
      :placeholder="$pgettext('auth.placeholder','Password')"
      v-model="v$.password.$model"
      :is-valid="!v$.password.$error"
      :label="$gettext('Password')"
      @keyup-enter="login">
      <template v-slot:invalid-feedback>
        <div class="invalid-feedback d-block" v-for="error of v$.password.$errors" :key="error.$uid">
          <div class="error-msg">{{ error.$message }}</div>
        </div>
      </template>
    </InputPassword>

    <PButton :loading="loading" type="button" color="primary" block @click="login">
      <translate translate-context="login.button">Login</translate>
    </PButton>

    <!-- Alternative login methods -->
    <div class="text-center mt-3">
      <CLink v-for="provider in alternativeProviders" :key="provider"
             @click="$emit('switch-provider', provider)"
             class="text-muted small d-block mb-1">
        <translate translate-context="login.alternative">{{ getProviderLabel(provider) }}</translate>
      </CLink>
    </div>
  </div>
</template>

<script>
import InputPassword from "@/components/InputPassword.vue";
import PButton from "@/components/PButton.vue";
import {useVuelidate} from "@vuelidate/core";
import {email$, required$} from "@/domain/core/utils/validators";
import {AuthProviders} from "@/domain/login/constant/authProviders";
import {mapActions, mapGetters} from "vuex";
import {methods} from "@/domain/login/mixin";

export default {
  name: 'PasswordLoginForm',
  components: {PButton, InputPassword},
  props: {
    allowRegister: {
      type: Boolean,
      default: true
    },
    initialEmail: {
      type: String,
      default: null
    }
  },
  setup() {
    return {v$: useVuelidate()}
  },
  data() {
    return {
      email: this.initialEmail,
      password: null,
      submitted: false,
      loading: false,
      error: null
    }
  },
  validations() {
    return {
      email: {required$, email$},
      password: {required$}
    }
  },
  computed: {
    ...mapGetters('login', ['authProviderFactory']),
    isValid() {
      return !this.v$.$invalid
    },
    alternativeProviders() {
      const provider = this.authProviderFactory.getProvider(AuthProviders.PASSWORD)
      return provider.getAlternatives((p) => this.authProviderFactory.isEnabled(p))
    }
  },
  mounted() {
    if (this.$refs.email) {
      this.$refs.email.$el.children[0].focus()
    }
  },
  watch: {
    initialEmail(val) {
      this.email = val
    }
  },
  methods: {
    ...methods,
    ...mapActions('login', ['authenticate']),

    async login() {
      if (!await this.v$.$validate()) return

      this.submitted = true
      if (!this.isValid) return

      this.error = null

      try {
        this.loading = true

        await this.authenticate({
          method: AuthProviders.PASSWORD,
          credentials: {
            email: this.email,
            password: this.password,
            allowRegister: this.allowRegister
          },
          remember: true
        })

        this.$emit('login-success')
      } catch (e) {
        if (await this.manageLoginError(e, this.email)) return
        this.error = this.$pgettext('login.error', 'Login error')
      } finally {
        this.loading = false
      }
    },

    getProviderLabel(provider) {
      switch (provider) {
        case AuthProviders.LDAP:
          return this.$pgettext('login.alternative', 'Use company credentials')
        case AuthProviders.EMAIL_TOKEN:
          return this.$pgettext('login.alternative', 'Use email and token')
        case AuthProviders.GOOGLE:
          return this.$pgettext('login.alternative', 'Use Google')
        case AuthProviders.FACEBOOK:
          return this.$pgettext('login.alternative', 'Use Facebook')
        case AuthProviders.APPLE:
          return this.$pgettext('login.alternative', 'Use Apple')
        default:
          return provider
      }
    }
  }
}
</script>

<style scoped>
.password-login-form {
  width: 100%;
}
</style>
